import { useCallback, useEffect } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import Loading from "../../../common/Loading";
import ArticleCard from "../../../common/ArticleCard";
import ArticleAds from "../ArticleAds";
import { getAllArticles } from "../../../../store/api/content";
import { IMAGE_URL_AZURE } from "../../../../constants/env";
import { useOrganisation } from "../../../../hooks/useOrganisation";
import { addCampaignStatistics } from "../../../../store/api/statistics";

const AdArticleCard = ({ ad, isMobile, isTablet }) => {
  if (!ad) return null;

  const adContent = ad.content || { ...ad, imageLink: ad.imageUrl };

  const handleAdClick = async () => {
    try {
      if (ad.id) {
        await addCampaignStatistics({ addId: ad.id });
      }
      if (adContent?.link) {
        window.open(adContent.link, "_blank", "noreferrer");
      }
    } catch (error) {
      console.error("Error tracking ad click:", error);
      if (adContent?.link) {
        window.open(adContent.link, "_blank", "noreferrer");
      }
    }
  };

  return (
    <ArticleCard
      article={{
        ...adContent,
      }}
      isMobile={isMobile}
      isTablet={isTablet}
      isAd={true}
      external={!!adContent?.link}
      link={adContent?.link}
      xsOverride={6}
      onClick={handleAdClick}
    />
  );
};

const PaginatedContent = ({ ads, skipFirstBanner = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const { primaryColor } = useOrganisation();

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ["articlesPagination"],
    queryFn: ({ pageParam = "" }) =>
      getAllArticles({ continuationToken: pageParam }),
    getNextPageParam: (lastPage) => lastPage.continuationToken || null,
  });

  const handleScroll = useCallback(() => {
    if (
      window.innerHeight + window.scrollY >=
      document.body.scrollHeight - 20
    ) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleBannerAdClick = async (adId, link) => {
    try {
      await addCampaignStatistics({ addId: adId });
      console.log("Banner ad click tracked successfully:", adId);
      window.open(link, "_blank", "noreferrer");
    } catch (error) {
      console.error("Error tracking banner ad click:", error);
      window.open(link, "_blank", "noreferrer");
    }
  };

  if (isLoading) return <Loading />;
  if (error)
    return <Typography variant="body1">Error loading articles</Typography>;

  const renderContent = () => {
    // Get all banner ads
    const allBannerAds = ads?.ads
      ?.filter((adGroup) => adGroup.type === "Banner")
      .reduce((allAds, currentItem) => {
        return allAds.concat(currentItem.ads);
      }, []);

    const displayableBannerAds =
      skipFirstBanner && allBannerAds?.length > 0
        ? allBannerAds.slice(1)
        : allBannerAds || [];

    // Get all carousel ads
    const allCarouselAds =
      ads?.ads
        ?.filter((adGroup) => adGroup.type === "Carousel")
        .reduce((allAds, currentItem) => {
          return allAds.concat(currentItem.ads);
        }, []) || [];

    // Get all list type ads
    const listTypeAds =
      ads?.ads
        ?.filter((adGroup) => adGroup.type === "List")
        .reduce((allAds, currentItem) => {
          return allAds.concat(currentItem.ads);
        }, []) || [];

    // Initialize ad indices
    let bannerAdIndex = 0;
    let carouselAdIndex = 0;
    let listAdIndex = 0;

    // Get all articles from paginated data
    const allArticles =
      data?.pages.flatMap((page) => page.paginatedContent) || [];
    const itemsPerRow = isMobile ? 2 : isTablet ? 2 : 4;

    // Prepare final content array
    const finalContent = [];

    // Add initial banner at the very top
    if (displayableBannerAds.length > 0) {
      const firstBannerAd = displayableBannerAds[0];
      finalContent.push(
        <Grid item xs={12} key="initial-banner">
          <Box
            component="div"
            onClick={() =>
              handleBannerAdClick(firstBannerAd.id, firstBannerAd.link)
            }
            sx={{
              width: "100%",
              maxHeight: "90px",
              cursor: "pointer",
              position: "relative",
              my: 2,
            }}
          >
            <Box
              component="img"
              src={`${firstBannerAd.imageUrl}?${IMAGE_URL_AZURE}`}
              alt={firstBannerAd.title || "Advertisement"}
              sx={{
                width: "100%",
                height: "90px",
                maxHeight: "150px",
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                bgcolor: primaryColor,
                color: "white",
                fontWeight: "medium",
                padding: "2px 8px",
                borderRadius: "4px",
                fontSize: "0.75rem",
              }}
            >
              Ad
            </Box>
          </Box>
        </Grid>
      );
      bannerAdIndex = 1; // Start from second banner for subsequent placements
    }

    // Process articles row by row with list ads integration
    let index = 0;
    let rowNumber = 0;

    while (index < allArticles.length) {
      if (rowNumber % 2 === 0 && listTypeAds.length > 0) {
        // Ad row with list-type ads
        const currentAdRowNumber = rowNumber;
        const alignment =
          Math.floor(currentAdRowNumber / 2) % 2 === 0 ? "left" : "right";
        const articlesInRow = allArticles.slice(
          index,
          index + (itemsPerRow - 1)
        );
        const ad =
          listTypeAds.length > 0
            ? listTypeAds[listAdIndex % listTypeAds.length]
            : null;

        const rowElements = [];
        const adElement = (
          <AdArticleCard
            key={`ad-${currentAdRowNumber}`}
            ad={ad}
            isMobile={isMobile}
            isTablet={isTablet}
          />
        );

        if (alignment === "left") rowElements.push(adElement);

        articlesInRow.forEach((article) => {
          rowElements.push(
            <ArticleCard
              key={article.id}
              article={article}
              isMobile={isMobile}
              isTablet={isTablet}
              xsOverride={6}
            />
          );
        });

        if (ad && alignment === "right") rowElements.push(adElement);
        if (ad) listAdIndex++;

        finalContent.push(...rowElements);
        index += articlesInRow.length;
      } else {
        // Regular row
        const articlesInRow = allArticles.slice(index, index + itemsPerRow);
        finalContent.push(
          ...articlesInRow.map((article) => (
            <ArticleCard
              key={article.id}
              article={article}
              isMobile={isMobile}
              isTablet={isTablet}
              xsOverride={6}
            />
          ))
        );
        index += articlesInRow.length;
      }

      rowNumber++;

      // Add carousel after row 1 (initial carousel)
      if (rowNumber === 1 && allCarouselAds.length > 0) {
        const carouselAdsToShow = allCarouselAds.slice(0, 5);

        if (carouselAdsToShow.length > 0) {
          const carouselAdGroup = {
            type: "Carousel",
            ads: carouselAdsToShow,
          };

          finalContent.push(
            <Grid item xs={12} key="initial-carousel">
              <ArticleAds data={[carouselAdGroup]} />
            </Grid>
          );

          carouselAdIndex = carouselAdsToShow.length;
        }
      }

      // Show Banner ads every 4 rows (starting from row 4)
      if (rowNumber % 4 === 0 && displayableBannerAds.length > 0) {
        const currentBannerAd =
          displayableBannerAds[bannerAdIndex % displayableBannerAds.length];

        if (currentBannerAd) {
          finalContent.push(
            <Grid item xs={12} key={`banner-ad-row-${rowNumber}`}>
              <Box
                component="div"
                onClick={() =>
                  handleBannerAdClick(currentBannerAd.id, currentBannerAd.link)
                }
                sx={{
                  width: "100%",
                  maxHeight: "90px",
                  cursor: "pointer",
                  position: "relative",
                  my: 2,
                }}
              >
                <Box
                  component="img"
                  src={`${currentBannerAd.imageUrl}?${IMAGE_URL_AZURE}`}
                  alt={currentBannerAd.title || "Advertisement"}
                  sx={{
                    width: "100%",
                    height: "90px",
                    maxHeight: "150px",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    bgcolor: primaryColor,
                    color: "white",
                    fontWeight: "medium",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "0.75rem",
                  }}
                >
                  Ad
                </Box>
              </Box>
            </Grid>
          );
          bannerAdIndex++;
        }
      }

      // Show Carousel ads every 5 rows (starting from row 5, since we already showed one after row 1)
      if (rowNumber % 5 === 0 && rowNumber > 1 && allCarouselAds.length > 0) {
        const carouselAdsToShow = allCarouselAds.slice(
          carouselAdIndex,
          carouselAdIndex + 5
        );

        if (carouselAdsToShow.length > 0) {
          const carouselAdGroup = {
            type: "Carousel",
            ads: carouselAdsToShow,
          };

          finalContent.push(
            <Grid item xs={12} key={`carousel-ad-row-${rowNumber}`}>
              <ArticleAds data={[carouselAdGroup]} />
            </Grid>
          );

          carouselAdIndex += carouselAdsToShow.length;
          // Reset carousel index if we've shown all carousel ads
          if (carouselAdIndex >= allCarouselAds.length) {
            carouselAdIndex = 0;
          }
        }
      }
    }

    return (
      <Grid container spacing={isMobile ? 1 : 2}>
        {finalContent}
        {isFetchingNextPage && <Loading />}
      </Grid>
    );
  };

  return (
    <section>
      <section className="sectionCenter">
        <Box sx={{ padding: isMobile ? 1 : 2 }}>{renderContent()}</Box>
      </section>
    </section>
  );
};

export default PaginatedContent;
